/**
 * TestLibrary.tsx — V102-8-7 · Test library (sibling of Common Controls).
 *
 * Route: /w/:slug/app/library/tests
 *
 * The customer-visible catalogue of assessment tests (ADR-0056). Each test
 * is a framework-neutral unit of work (`identity.accounts.inventory`) that
 * serves one or more objectives across frameworks (CMMC, SOC 2, …). The
 * library shows:
 *   - test key + title + domain (identity, change, logging, …);
 *   - framework refs served (chips with the framework id as a title);
 *   - reuse count ("serves N objectives across M frameworks");
 *   - primary strategy (plain-English phrase, not a jargon token);
 *   - automation (high / partial / manual);
 *   - window / sample size (point_in_time / window / sample).
 *
 * Open opens a right-side drawer with the full `met_criteria`, typical gap,
 * methods, evidence sources, and the list of objectives served. The pinned
 * version hash is hidden behind a "Verify version" affordance (modal) — per
 * build-list acceptance, hashes NEVER on primary chrome.
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Drawer,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
} from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { useDialogMobileOnly } from '../lib/use-dialog-mobile-only.js';
import {
  listLibraryTests,
  getLibraryTest,
  type LibraryTestRow,
  type LibraryTestDetail,
} from '../lib/api';
import { fmtDate } from '../lib/format';

const STRATEGY_LABEL: Record<string, string> = {
  configuration_state: 'Read current config',
  behavior_over_period: 'Observe behaviour over a period',
  process_sample: 'Sample process records',
  cross_source_reconciliation: 'Reconcile across sources',
  adversarial_test: 'Run an adversarial test',
  document_and_interview: 'Review documents, interview owners',
  inherited_from_provider: 'Inherit from the provider',
};

const AUTOMATION_LABEL: Record<string, string> = {
  high: 'Automated',
  partial: 'Partial',
  manual: 'Manual',
};

const WINDOW_LABEL: Record<string, (days: number | null, size: number | null) => string> = {
  point_in_time: () => 'Point in time',
  window: (days) => (days ? `${days}-day window` : 'Window'),
  sample: (_d, size) => (size ? `${size}-item sample` : 'Sample'),
  current_document: () => 'Current document',
};

const DOMAIN_LABEL: Record<string, string> = {
  identity: 'Identity',
  change: 'Change management',
  logging: 'Logging',
  vulnerability: 'Vulnerability',
  endpoint: 'Endpoint',
  network: 'Network',
  data_protection: 'Data protection',
  incident: 'Incident response',
  training: 'Training',
  policy: 'Policy & governance',
  physical: 'Physical',
  risk: 'Risk management',
};

const FRAMEWORK_LABEL: Record<string, string> = {
  'cmmc-l1': 'CMMC L1',
  'cmmc-l2': 'CMMC L2',
  'cmmc-l3': 'CMMC L3',
  soc2: 'SOC 2',
  'soc2-ai': 'SOC 2 AI',
  'fedramp-cr26': 'FedRAMP CR26',
  'fedramp-20x-pilot': 'FedRAMP 20x',
  'nist-800-53': 'NIST 800-53',
  'iso-42001': 'ISO 42001',
  'nist-ai-rmf': 'NIST AI RMF',
  'eu-ai-act': 'EU AI Act',
  gdpr: 'GDPR',
  hipaa: 'HIPAA',
};

function frameworkIdOf(ref: string): string {
  const i = ref.indexOf(':');
  return i < 0 ? ref : ref.slice(0, i);
}

function windowLabel(row: LibraryTestRow): string {
  const fn = WINDOW_LABEL[row.window_kind];
  return fn ? fn(row.window_days, row.window_size) : row.window_kind;
}

export function TestLibrary() {
  // Workspace scope is enforced by useWorkspaceQuery; the slug is already
  // injected into every library-tests query key below.
  useWorkspace();

  const [domain, setDomain] = useState<string>('');
  const [framework, setFramework] = useState<string>('');
  const [automation, setAutomation] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [openTest, setOpenTest] = useState<string | null>(null);

  const filterKey = `${domain}|${framework}|${automation}|${search}`;
  const q = useWorkspaceQuery(
    (s) => queryKeys.libraryTests(s, filterKey),
    (s) => listLibraryTests(s, { domain: domain || undefined, framework: framework || undefined, automation: automation || undefined, q: search || undefined }),
  );

  const resp = q.data;
  const rows = resp?.tests ?? [];

  const domains = useMemo(() => resp?.domains ?? [], [resp]);
  const frameworks = useMemo(() => resp?.frameworks ?? [], [resp]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '82rem', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <PageHeader
        title="Test library"
        subtitle="Framework-neutral tests that cover controls across CMMC, SOC 2, FedRAMP, and more. One test, many frameworks."
      />

      {q.error ? <Alert variant="error">{String(q.error)}</Alert> : null}

      <section
        aria-label="Filters"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 1fr) 180px 180px 180px',
          gap: 'var(--space-3)',
          padding: 'var(--space-3) var(--space-4)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
        }}
      >
        <Field id="lib-search" label="Search">
          <input
            id="lib-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="test key, title, or domain"
            style={{
              width: '100%',
              padding: 'var(--space-2) var(--space-3)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-input, var(--radius-card))',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-2xs)',
            }}
          />
        </Field>
        <Field id="lib-domain" label="Domain">
          <Select id="lib-domain" value={domain} onChange={(e) => setDomain(e.target.value)}>
            <option value="">All domains</option>
            {domains.map((d) => <option key={d} value={d}>{DOMAIN_LABEL[d] ?? d}</option>)}
          </Select>
        </Field>
        <Field id="lib-framework" label="Framework">
          <Select id="lib-framework" value={framework} onChange={(e) => setFramework(e.target.value)}>
            <option value="">All frameworks</option>
            {frameworks.map((f) => <option key={f} value={f}>{FRAMEWORK_LABEL[f] ?? f}</option>)}
          </Select>
        </Field>
        <Field id="lib-automation" label="Automation">
          <Select id="lib-automation" value={automation} onChange={(e) => setAutomation(e.target.value)}>
            <option value="">Any level</option>
            <option value="high">Automated</option>
            <option value="partial">Partial</option>
            <option value="manual">Manual</option>
          </Select>
        </Field>
      </section>

      {q.loading ? (
        <Spinner label="Loading tests" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No tests match these filters"
          description="Clear a filter or widen your search to see the test catalogue."
        />
      ) : (
        <>
          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            {resp?.total ?? rows.length} test{(resp?.total ?? rows.length) === 1 ? '' : 's'}
          </div>
          <div
            role="table"
            aria-label="Assessment tests"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-card)',
              background: 'var(--surface)',
              overflow: 'hidden',
            }}
          >
            <div
              role="row"
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 1.5fr) minmax(200px, 2fr) 110px 120px 120px',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--surface-alt, var(--surface-elevated))',
                borderBottom: '1px solid var(--border)',
                fontSize: 'var(--font-size-3xs)',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              <div role="columnheader">Test</div>
              <div role="columnheader">Frameworks served</div>
              <div role="columnheader">Strategy</div>
              <div role="columnheader">Automation</div>
              <div role="columnheader">Window</div>
            </div>
            {rows.map((row) => (
              <TestRow key={row.test_key} row={row} onOpen={() => setOpenTest(row.test_key)} />
            ))}
          </div>
        </>
      )}

      <TestDrawer testKey={openTest} onClose={() => setOpenTest(null)} />
    </div>
  );
}

function TestRow({ row, onOpen }: { row: LibraryTestRow; onOpen: () => void }) {
  const label = STRATEGY_LABEL[row.primary_strategy] ?? row.primary_strategy;
  const auto = AUTOMATION_LABEL[row.automation] ?? row.automation;
  return (
    <div
      role="row"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(220px, 1.5fr) minmax(200px, 2fr) 110px 120px 120px',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        alignItems: 'start',
      }}
    >
      <div role="cell" style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
        <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text)', fontWeight: 500 }}>{row.title}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
          {row.test_key}
        </span>
        {row.domain ? (
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
            {DOMAIN_LABEL[row.domain] ?? row.domain}
          </span>
        ) : null}
      </div>
      <div role="cell" style={{ display: 'grid', gap: 'var(--space-1)', minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {row.framework_refs.slice(0, 6).map((ref) => {
            const fw = frameworkIdOf(ref);
            return (
              <span
                key={ref}
                title={FRAMEWORK_LABEL[fw] ?? fw}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1px 8px',
                  borderRadius: 'var(--radius-chip)',
                  border: '1px solid var(--border-strong)',
                  background: 'var(--surface)',
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  whiteSpace: 'nowrap',
                }}
              >
                {ref}
              </span>
            );
          })}
          {row.framework_refs.length > 6 ? (
            <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', alignSelf: 'center' }}>
              +{row.framework_refs.length - 6} more
            </span>
          ) : null}
        </div>
        <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
          Serves {row.objectives_served} objective{row.objectives_served === 1 ? '' : 's'} across {row.frameworks_count} framework{row.frameworks_count === 1 ? '' : 's'}
        </span>
      </div>
      <div role="cell" style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text)' }}>{label}</div>
      <div role="cell">
        <Badge tone={row.automation === 'high' ? 'accent' : 'neutral'}>{auto}</Badge>
      </div>
      <div role="cell" style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text)' }}>{windowLabel(row)}</div>
    </div>
  );
}

function TestDrawer({ testKey, onClose }: { testKey: string | null; onClose: () => void }) {
  const q = useWorkspaceQuery(
    (s) => queryKeys.libraryTest(s, testKey ?? '__none__'),
    (s) => (testKey ? getLibraryTest(s, testKey) : Promise.resolve({ detail: null as unknown as LibraryTestDetail })),
    { enabled: !!testKey },
  );
  const detail = testKey ? q.data?.detail ?? null : null;
  const [verifyOpen, setVerifyOpen] = useState(false);

  return (
    <Drawer open={!!testKey} onClose={onClose} label="Test detail" modal>
      <aside
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100dvh',
          width: 'min(560px, 100vw)',
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          overflowY: 'auto',
          padding: 'var(--space-4)',
          display: 'grid',
          gap: 'var(--space-3)',
          gridTemplateColumns: 'minmax(0, 1fr)',
          alignContent: 'start',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
          <div style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
            <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
              {detail?.test.title ?? 'Loading…'}
            </span>
            {detail ? (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                {detail.test.test_key}
              </span>
            ) : null}
          </div>
          <Button variant="ghost" compact onClick={onClose} aria-label="Close test detail">Close</Button>
        </div>

        {q.loading ? <Spinner label="Loading test" /> : null}
        {q.error ? <Alert variant="error">{String(q.error)}</Alert> : null}

        {detail ? (
          <>
            <section style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <SectionHeading>Met criteria</SectionHeading>
              <p style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)', lineHeight: 1.5 }}>
                {detail.met_criteria}
              </p>
            </section>

            {detail.typical_gap ? (
              <section style={{ display: 'grid', gap: 'var(--space-1)' }}>
                <SectionHeading>Typical gap</SectionHeading>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)', lineHeight: 1.5 }}>
                  {detail.typical_gap}
                </p>
              </section>
            ) : null}

            <section style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <SectionHeading>Methods</SectionHeading>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {detail.test.methods.map((m) => <Badge key={m}>{m}</Badge>)}
              </div>
            </section>

            <section style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <SectionHeading>Evidence sources</SectionHeading>
              <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
                {detail.evidence_sources.map((s) => <li key={s}>{s}</li>)}
                {detail.evidence_sources.length === 0 ? (
                  <li style={{ color: 'var(--text-dim)', listStyle: 'none', marginLeft: 'calc(-1 * var(--space-4))' }}>No evidence sources listed.</li>
                ) : null}
              </ul>
            </section>

            <section style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <SectionHeading>
                Serves {detail.objectives.length} objective{detail.objectives.length === 1 ? '' : 's'}
              </SectionHeading>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 'var(--space-1)' }}>
                {detail.objectives.map((o) => (
                  <li
                    key={`${o.framework_id}:${o.objective_id}`}
                    style={{
                      padding: 'var(--space-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-card)',
                      background: 'var(--surface-alt, var(--surface))',
                      display: 'grid',
                      gap: '2px',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text)' }}>
                        {o.objective_id}
                      </span>
                      <Badge tone={o.role === 'primary' ? 'accent' : 'neutral'}>
                        {o.role === 'primary' ? 'primary' : 'corroborating'}
                      </Badge>
                      <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                        {FRAMEWORK_LABEL[o.framework_id] ?? o.framework_id}
                      </span>
                    </div>
                    <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>{o.statement}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section style={{
              padding: 'var(--space-3)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-card)',
              background: 'var(--surface-alt, var(--surface))',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}>
              <div style={{ display: 'grid', gap: '2px' }}>
                <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>
                  Version {detail.version.version} · {detail.version.approved_at ? `approved ${fmtDate(detail.version.approved_at)}` : 'draft'}
                </span>
                <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                  Approved by {detail.version.approved_by ?? '—'}
                </span>
              </div>
              <Button variant="secondary" compact onClick={() => setVerifyOpen(true)}>
                Verify version
              </Button>
            </section>
          </>
        ) : null}

        {verifyOpen && detail ? (
          <VerifyModal
            onClose={() => setVerifyOpen(false)}
            testKey={detail.test.test_key}
            version={detail.version}
          />
        ) : null}
      </aside>
    </Drawer>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{
      margin: 0,
      fontSize: 'var(--font-size-3xs)',
      fontWeight: 600,
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    }}>
      {children}
    </h3>
  );
}

function VerifyModal({ onClose, testKey, version }: {
  onClose: () => void;
  testKey: string;
  version: LibraryTestDetail['version'];
}) {
  const dialogRef = useDialogMobileOnly<HTMLDivElement>(true);
  async function copy(value: string) {
    try { await navigator.clipboard.writeText(value); } catch { /* clipboard may be unavailable */ }
  }
  return (
    <div
      ref={dialogRef}
      aria-label="Verify test version"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay, rgba(0,0,0,0.5))',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-4)',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-4)',
          display: 'grid',
          gap: 'var(--space-3)',
          maxWidth: '520px',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>Verify version</h2>
          <Button variant="ghost" compact onClick={onClose} aria-label="Close verify dialog">Close</Button>
        </div>
        <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          This hash pins what {testKey} version {version.version} judges. Record it alongside your working papers.
        </p>
        <HashRow label="Version id" value={version.test_version_id} onCopy={copy} />
        <HashRow label="Version hash" value={version.version_hash} onCopy={copy} />
        {version.rationale ? (
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            <SectionHeading>Approval rationale</SectionHeading>
            <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--text)' }}>{version.rationale}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HashRow({ label, value, onCopy }: { label: string; value: string; onCopy: (v: string) => void }) {
  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <span style={{
        fontSize: 'var(--font-size-3xs)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        fontWeight: 600,
      }}>
        {label}
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <code
          style={{
            flex: 1,
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            padding: 'var(--space-2)',
            background: 'var(--surface-alt, var(--surface-elevated))',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-input, var(--radius-card))',
            color: 'var(--text)',
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </code>
        <Button variant="ghost" compact onClick={() => onCopy(value)} aria-label={`Copy ${label.toLowerCase()}`}>
          Copy
        </Button>
      </div>
    </div>
  );
}
